import { Doc } from '@dadohr/shared/domainTypes/document';
import { GoogleSheetsIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/googleSheets';
import styled from '@emotion/styled';
import {
  Button,
  ButtonBase,
  Dialog,
  DialogContent,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useEffect, useRef } from 'react';
import { Functionality } from '../..';
import { AlertBox } from '../../../../../../components/AlertBox';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { InfoBox } from '../../../../../../components/InfoBox';
import { LinkExternal } from '../../../../../../components/LinkExternal';
import { Loader } from '../../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../../components/SomethingWentWrong';
import ENVS from '../../../../../../env.json';
import { useErrorLogger } from '../../../../../../hooks/useErrorLogger';
import { useTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { LightMdToJsx } from '../../../../../../services/formatter';
import { PageOAuthRedirectProps } from '../../../../../OAuth/pages/Redirect';
import { IntegrationData } from '../../components/IntegrationData';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  masked
} from '../common';
import { OAuthSetupDialog } from '../oauth';
import {
  removeGoogleSheetsConfig,
  useGoogleSheetsConfig,
  useGoogleSheetsCurrentUser
} from './service';

const NAME = 'Google Workspace';

export const GOOGLE_SHEETS_OAUTH_REDIRECT_PROPS: PageOAuthRedirectProps = {
  integrationName: NAME,
  cloudFn: 'googleSheets_exchangeCodeAndStoreCredentials'
};

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const OAUTH = ENVS.googleSheets.oauth;

const GOOGLE_SCOPES = [
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/spreadsheets',
  'https://www.googleapis.com/auth/drive.file',
  'https://www.googleapis.com/auth/calendar',
  'https://www.googleapis.com/auth/calendar.events',
  'https://www.googleapis.com/auth/calendar.readonly',
  'https://www.googleapis.com/auth/admin.directory.group',
  'https://www.googleapis.com/auth/documents'
];

const InitialSetupDialog = ({
  orgId,
  open,
  onClose
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const scopes = GOOGLE_SCOPES;
  return (
    <OAuthSetupDialog
      orgId={orgId}
      open={open}
      onClose={onClose}
      name={NAME}
      oauthUrl={'https://accounts.google.com/o/oauth2/v2/auth'}
      clientId={OAUTH.clientId}
      redirectUri={OAUTH.redirectUri}
      otherParams={{
        scope: scopes.join(' '),
        access_type: 'offline',
        prompt: 'consent' // this way we can guarantee that the refresh token is returned
      }}
      customButton={(href) => {
        return (
          <ButtonBase href={href} target="_blank">
            <img
              height={42}
              alt="Sign In with Google"
              src={
                '/images/integrations-logos/google-signin-buttons/btn_google_signin_dark_normal_web@2x.png'
              }
            />
          </ButtonBase>
        );
      }}
    >
      <Alert severity="info" icon={false}>
        {t('org.integrations.googleSheets.setupInfo.p1')}
      </Alert>
      <Typography component="p" variant="body1">
        {t('org.integrations.googleSheets.setupInfo.p2')}
      </Typography>

      <Typography component="p" variant="caption">
        {t('org.integrations.googleSheets.setupInfo.p2-1')}{' '}
        <LinkExternal
          color="primary"
          href="https://support.google.com/a/answer/7281227"
        >
          {t('org.integrations.googleSheets.setupInfo.p2-1-link')}
        </LinkExternal>
      </Typography>
      <InfoBox>{t('org.integrations.googleSheets.setupInfo.p3')}</InfoBox>
    </OAuthSetupDialog>
  );
};

const ExistingSetupDialog = ({
  config,
  open,
  onClose
}: {
  config: Doc<GoogleSheetsIntegrationConfig>;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  const [user, loading, error] = useGoogleSheetsCurrentUser(config.data.orgId);
  useErrorLogger('google integration', error);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        {loading && !user && <Loader height={300} />}
        {user && (
          <>
            <Alert severity="info" icon={false}>
              {t('org.integrations.googleSheets.setupInfo.p1')}
            </Alert>

            <AlertBox color="yellow">
              <LightMdToJsx>
                {t('org.integrations.googleSheets.accessWarning', {
                  email: user.email
                })}
              </LightMdToJsx>
            </AlertBox>

            <IntegrationData>
              <strong>{t('org.integrations.common.email')}</strong>
              <div>{user.email || ''}</div>
              <strong>{t('org.integrations.common.accessToken')}</strong>
              <div>{masked(config.data.accessToken)}</div>
              <strong>{t('org.integrations.common.refreshToken')}</strong>
              <div>{masked(config.data.refreshToken)}</div>
              <strong>{t('org.integrations.common.scopes')}</strong>
              <div>{config.data.scope.split(' ').join('\n')}</div>
            </IntegrationData>
          </>
        )}
        {error && <SomethingWentWrong />}
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeGoogleSheetsConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

interface GoogleSetupDialogProps extends IntegrationSetupDialogProperties {
  orgId: string;
  config: Doc<GoogleSheetsIntegrationConfig> | null;
}

const SetupDialog = ({
  config,
  onClose,
  orgId,
  onSuccessfulIntegration,
  open
}: GoogleSetupDialogProps) => {
  const setup = !!(config?.data.accessToken && config?.data.refreshToken);
  const previousConfig = useRef<Doc<GoogleSheetsIntegrationConfig> | null>();

  //This is a bit of a hack to detect when the config active status changes so we can show the modal dialog
  //here even if the integration is completed on another browser tab.
  useEffect(() => {
    if (
      previousConfig.current?.data &&
      config?.data.active &&
      !previousConfig.current?.data.active
    ) {
      onSuccessfulIntegration();
    }
    previousConfig.current = config;
  }, [config, onSuccessfulIntegration]);

  return (
    <>
      {!setup && (
        <InitialSetupDialog orgId={orgId} open={open} onClose={onClose} />
      )}
      {setup && config && (
        <ExistingSetupDialog config={config} open={open} onClose={onClose} />
      )}
    </>
  );
};

export const GoogleSheetsSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useGoogleSheetsConfig(org.id, userId);
  const cfg = config || null;

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl="/images/integrations-logos/google-workspace-logo.png"
      active={!!cfg?.data.active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          config={cfg}
          orgId={org.id}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
